/**
 * USRN Portal Page (DRR-220)
 *
 * External reviewer interface for care plan clinical review.
 * Accessed via magic link — NO FERP account required.
 *
 * Security:
 *   - Token-based auth (not FERP session)
 *   - Shows ONLY tokenized data (no PHI)
 *   - Cannot see: real names, facility name, DoN identity
 *   - All actions logged as T1 audit events
 *   - 24-hour session expiry
 *
 * Data flow:
 *   1. USRN company receives webhook with magic link
 *   2. Reviewer clicks link → lands here
 *   3. Reviews tokenized care plan draft
 *   4. Approves or requests changes
 *   5. Result flows back to DoN queue (deanonymized there)
 */

import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Container,
  Paper,
  Title,
  Text,
  Stack,
  Alert,
  Button,
  Group,
  Badge,
  Card,
  Divider,
  Textarea,
  LoadingOverlay,
  Center,
  ThemeIcon,
  List,
  SimpleGrid,
  Checkbox,
  Modal,
} from '@mantine/core';
import {
  IconAlertTriangle,
  IconCheck,
  IconX,
  IconShieldCheck,
  IconClock,
  IconFileText,
  IconPill,
  IconStethoscope,
  IconClipboardCheck,
  IconSend,
  IconLock,
} from '@tabler/icons-react';
import { gql } from '@apollo/client';
import { useQuery, useMutation } from '@apollo/client/react';

// =============================================================================
// Status Constants (USRN Portal uses its own status enum, not main FERP)
// This is intentional — USRN portal is isolated from main FERP types
// =============================================================================

const UsrnReviewStatus = {
  PendingUsrnReview: 'PENDING_USRN_REVIEW',
  UsrnChangesRequested: 'USRN_CHANGES_REQUESTED',
  PendingDonReview: 'PENDING_DON_REVIEW',
} as const;

// =============================================================================
// GraphQL Operations
// =============================================================================

const CHECK_SESSION = gql`
  query UsrnSessionValid {
    usrnSessionValid
  }
`;

const GET_ASSIGNED_REVIEWS = gql`
  query UsrnAssignedReviews {
    usrnAssignedReviews {
      reviewId
      subjectToken
      facilityToken
      adlAssessment {
        bathing
        dressing
        mobility
        toileting
        eating
        observations
      }
      medications {
        nameToken
        dosage
        frequency
        route
      }
      conditions
      generatedPlan
      priorPlanSummary
      reviewDeadline
      status
    }
  }
`;

const SUBMIT_REVIEW = gql`
  mutation SubmitUsrnReview($input: UsrnReviewDecisionInput!) {
    submitUsrnReview(input: $input) {
      success
      message
      nextReviewAvailable
    }
  }
`;

// =============================================================================
// Types
// =============================================================================

interface TokenizedReview {
  reviewId: string;
  subjectToken: string;
  facilityToken: string;
  adlAssessment: {
    bathing: string;
    dressing: string;
    mobility: string;
    toileting: string;
    eating: string;
    observations: string;
  };
  medications: Array<{
    nameToken: string;
    dosage: string;
    frequency: string;
    route: string;
  }>;
  conditions: string[];
  generatedPlan: string;
  priorPlanSummary?: string;
  reviewDeadline: string;
  status: string;
}

// =============================================================================
// Component
// =============================================================================

export function UsrnPortalPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [selectedReview, setSelectedReview] = useState<TokenizedReview | null>(null);
  const [notes, setNotes] = useState('');
  const [changeRequests, setChangeRequests] = useState<string[]>([]);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingDecision, setPendingDecision] = useState<'APPROVED' | 'CHANGES_REQUESTED' | null>(null);

  // Check session validity
  const { data: sessionData, loading: sessionLoading, error: sessionError } = useQuery(CHECK_SESSION, {
    context: {
      headers: {
        'x-usrn-session': token,
      },
    },
    skip: !token,
  });

  // Load assigned reviews
  const { data: reviewsData, loading: reviewsLoading, refetch } = useQuery(GET_ASSIGNED_REVIEWS, {
    context: {
      headers: {
        'x-usrn-session': token,
      },
    },
    skip: !token || !sessionData?.usrnSessionValid,
  });

  // Submit review mutation
  const [submitReview, { loading: submitting }] = useMutation(SUBMIT_REVIEW, {
    context: {
      headers: {
        'x-usrn-session': token,
      },
    },
    onCompleted: (data) => {
      if (data.submitUsrnReview.success) {
        setSelectedReview(null);
        setNotes('');
        setChangeRequests([]);
        refetch();
      }
    },
  });

  // Handle decision submission
  const handleSubmit = useCallback(async () => {
    if (!selectedReview || !pendingDecision) return;

    await submitReview({
      variables: {
        input: {
          reviewId: selectedReview.reviewId,
          decision: pendingDecision,
          notes,
          changeRequests: pendingDecision === 'CHANGES_REQUESTED' ? changeRequests : null,
        },
      },
    });

    setShowConfirmModal(false);
    setPendingDecision(null);
  }, [selectedReview, pendingDecision, notes, changeRequests, submitReview]);

  // No token provided
  if (!token) {
    return (
      <Container size="sm" py="xl">
        <Paper p="xl" withBorder>
          <Center>
            <Stack align="center" gap="md">
              <ThemeIcon size={60} radius="xl" color="red" variant="light">
                <IconLock size={30} />
              </ThemeIcon>
              <Title order={2}>Access Denied</Title>
              <Text c="dimmed" ta="center">
                This page requires a valid review link.
                Please use the link provided by your USRN coordinator.
              </Text>
            </Stack>
          </Center>
        </Paper>
      </Container>
    );
  }

  // Loading session
  if (sessionLoading) {
    return (
      <Container size="sm" py="xl">
        <Paper p="xl" withBorder>
          <Center>
            <Stack align="center" gap="md">
              <LoadingOverlay visible />
              <Text>Validating session...</Text>
            </Stack>
          </Center>
        </Paper>
      </Container>
    );
  }

  // Invalid session
  if (sessionError || !sessionData?.usrnSessionValid) {
    return (
      <Container size="sm" py="xl">
        <Paper p="xl" withBorder>
          <Center>
            <Stack align="center" gap="md">
              <ThemeIcon size={60} radius="xl" color="orange" variant="light">
                <IconClock size={30} />
              </ThemeIcon>
              <Title order={2}>Session Expired</Title>
              <Text c="dimmed" ta="center">
                Your review session has expired or is invalid.
                Please contact your USRN coordinator for a new link.
              </Text>
            </Stack>
          </Center>
        </Paper>
      </Container>
    );
  }

  const reviews: TokenizedReview[] = reviewsData?.usrnAssignedReviews || [];

  return (
    <Container size="xl" py="xl">
      {/* Header */}
      <Paper p="lg" mb="lg" withBorder>
        <Group justify="space-between">
          <Group>
            <ThemeIcon size={40} radius="md" color="blue" variant="light">
              <IconShieldCheck size={24} />
            </ThemeIcon>
            <div>
              <Title order={3}>USRN Clinical Review Portal</Title>
              <Text size="sm" c="dimmed">
                Secure tokenized review — No patient identifiers visible
              </Text>
            </div>
          </Group>
          <Badge color="green" size="lg" variant="light">
            {reviews.length} Review{reviews.length !== 1 ? 's' : ''} Assigned
          </Badge>
        </Group>
      </Paper>

      {/* Privacy Notice */}
      <Alert
        icon={<IconLock size={20} />}
        color="blue"
        mb="lg"
        title="Privacy Protection Active"
      >
        All patient information is tokenized. You are reviewing clinical protocols,
        not identifiable patient data. Your identity is also protected from the
        approving facility.
      </Alert>

      {reviewsLoading ? (
        <Paper p="xl" withBorder>
          <Center>
            <LoadingOverlay visible />
          </Center>
        </Paper>
      ) : reviews.length === 0 ? (
        <Paper p="xl" withBorder>
          <Center>
            <Stack align="center" gap="md">
              <ThemeIcon size={60} radius="xl" color="green" variant="light">
                <IconCheck size={30} />
              </ThemeIcon>
              <Title order={3}>All Reviews Complete</Title>
              <Text c="dimmed">
                You have completed all assigned reviews. Thank you!
              </Text>
            </Stack>
          </Center>
        </Paper>
      ) : !selectedReview ? (
        /* Review List */
        <Stack gap="md">
          {reviews.map((review) => (
            <Card
              key={review.reviewId}
              withBorder
              padding="lg"
              style={{ cursor: 'pointer' }}
              onClick={() => setSelectedReview(review)}
            >
              <Group justify="space-between">
                <Group>
                  <ThemeIcon size={40} radius="md" variant="light">
                    <IconFileText size={24} />
                  </ThemeIcon>
                  <div>
                    <Text fw={500}>Care Plan Review</Text>
                    <Text size="sm" c="dimmed">
                      Subject: {review.subjectToken} • {review.medications.length} medications
                    </Text>
                  </div>
                </Group>
                <Group>
                  <Badge color={review.status === UsrnReviewStatus.PendingUsrnReview ? 'blue' : 'gray'}>
                    {review.status.replace(/_/g, ' ')}
                  </Badge>
                  <Button variant="light" rightSection={<IconClipboardCheck size={16} />}>
                    Review
                  </Button>
                </Group>
              </Group>
            </Card>
          ))}
        </Stack>
      ) : (
        /* Review Detail */
        <Stack gap="lg">
          <Button
            variant="subtle"
            leftSection={<IconX size={16} />}
            onClick={() => {
              setSelectedReview(null);
              setNotes('');
              setChangeRequests([]);
            }}
          >
            Back to List
          </Button>

          {/* Subject Info (Tokenized) */}
          <Paper p="lg" withBorder>
            <Group justify="space-between" mb="md">
              <Title order={4}>Subject Information</Title>
              <Badge color="blue" variant="outline">Tokenized</Badge>
            </Group>
            <SimpleGrid cols={2}>
              <Text><strong>Subject:</strong> {selectedReview.subjectToken}</Text>
              <Text><strong>Facility:</strong> {selectedReview.facilityToken}</Text>
            </SimpleGrid>
          </Paper>

          {/* ADL Assessment */}
          <Paper p="lg" withBorder>
            <Title order={4} mb="md">ADL Assessment</Title>
            <SimpleGrid cols={3}>
              <Card withBorder padding="sm">
                <Text size="sm" c="dimmed">Bathing</Text>
                <Text fw={500}>{selectedReview.adlAssessment.bathing}</Text>
              </Card>
              <Card withBorder padding="sm">
                <Text size="sm" c="dimmed">Dressing</Text>
                <Text fw={500}>{selectedReview.adlAssessment.dressing}</Text>
              </Card>
              <Card withBorder padding="sm">
                <Text size="sm" c="dimmed">Mobility</Text>
                <Text fw={500}>{selectedReview.adlAssessment.mobility}</Text>
              </Card>
              <Card withBorder padding="sm">
                <Text size="sm" c="dimmed">Toileting</Text>
                <Text fw={500}>{selectedReview.adlAssessment.toileting}</Text>
              </Card>
              <Card withBorder padding="sm">
                <Text size="sm" c="dimmed">Eating</Text>
                <Text fw={500}>{selectedReview.adlAssessment.eating}</Text>
              </Card>
            </SimpleGrid>
            <Divider my="md" />
            <Text size="sm" c="dimmed">Observations</Text>
            <Text>{selectedReview.adlAssessment.observations}</Text>
          </Paper>

          {/* Medications */}
          <Paper p="lg" withBorder>
            <Group mb="md">
              <IconPill size={20} />
              <Title order={4}>Medications ({selectedReview.medications.length})</Title>
            </Group>
            <Stack gap="xs">
              {selectedReview.medications.map((med, idx) => (
                <Card key={idx} withBorder padding="sm">
                  <Group justify="space-between">
                    <Text fw={500}>{med.nameToken}</Text>
                    <Group gap="xs">
                      <Badge variant="outline">{med.dosage}</Badge>
                      <Badge variant="outline">{med.frequency}</Badge>
                      <Badge variant="outline">{med.route}</Badge>
                    </Group>
                  </Group>
                </Card>
              ))}
            </Stack>
          </Paper>

          {/* Conditions */}
          <Paper p="lg" withBorder>
            <Group mb="md">
              <IconStethoscope size={20} />
              <Title order={4}>Conditions/Diagnoses</Title>
            </Group>
            <Group gap="xs">
              {selectedReview.conditions.map((condition, idx) => (
                <Badge key={idx} size="lg" variant="light">
                  {condition}
                </Badge>
              ))}
            </Group>
          </Paper>

          {/* Generated Care Plan */}
          <Paper p="lg" withBorder>
            <Title order={4} mb="md">AI-Generated Care Plan Draft</Title>
            <Paper p="md" bg="gray.0" style={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}>
              {selectedReview.generatedPlan}
            </Paper>
          </Paper>

          {/* Prior Plan (if exists) */}
          {selectedReview.priorPlanSummary && (
            <Paper p="lg" withBorder>
              <Title order={4} mb="md">Prior Care Plan Summary</Title>
              <Text>{selectedReview.priorPlanSummary}</Text>
            </Paper>
          )}

          {/* Review Form */}
          <Paper p="lg" withBorder>
            <Title order={4} mb="md">Your Clinical Review</Title>

            <Textarea
              label="Clinical Notes"
              description="Your assessment of the care plan draft. This will be visible to the approving authority."
              placeholder="Enter your clinical observations, recommendations, or concerns..."
              minRows={4}
              value={notes}
              onChange={(e) => setNotes(e.currentTarget.value)}
              mb="md"
            />

            <Divider my="md" />

            <Group justify="flex-end" gap="md">
              <Button
                variant="outline"
                color="orange"
                leftSection={<IconAlertTriangle size={18} />}
                onClick={() => {
                  setPendingDecision('CHANGES_REQUESTED');
                  setShowConfirmModal(true);
                }}
                disabled={!notes.trim()}
              >
                Request Changes
              </Button>
              <Button
                color="green"
                leftSection={<IconCheck size={18} />}
                onClick={() => {
                  setPendingDecision('APPROVED');
                  setShowConfirmModal(true);
                }}
              >
                Approve Care Plan
              </Button>
            </Group>
          </Paper>
        </Stack>
      )}

      {/* Confirmation Modal */}
      <Modal
        opened={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        title={pendingDecision === 'APPROVED' ? 'Confirm Approval' : 'Confirm Change Request'}
      >
        <Stack>
          <Text>
            {pendingDecision === 'APPROVED'
              ? 'You are about to approve this care plan draft. It will be forwarded to the facility for final authorization.'
              : 'You are requesting changes to this care plan. Please ensure your notes clearly describe what needs to be modified.'}
          </Text>

          {pendingDecision === 'CHANGES_REQUESTED' && (
            <Textarea
              label="Specific Changes Requested"
              placeholder="List specific items that need modification..."
              minRows={3}
              value={changeRequests.join('\n')}
              onChange={(e) => setChangeRequests(e.currentTarget.value.split('\n').filter(Boolean))}
            />
          )}

          <Group justify="flex-end" gap="md">
            <Button variant="outline" onClick={() => setShowConfirmModal(false)}>
              Cancel
            </Button>
            <Button
              color={pendingDecision === 'APPROVED' ? 'green' : 'orange'}
              onClick={handleSubmit}
              loading={submitting}
              leftSection={<IconSend size={18} />}
            >
              Confirm {pendingDecision === 'APPROVED' ? 'Approval' : 'Change Request'}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Container>
  );
}

export default UsrnPortalPage;
